<template>
	<div class="DisciplineFilter">
		<SearchableRadioFilter
			:items="items"
			:options="options"
			:modelValue="selectedId"
			placeholder="Search by Discipline"
			groupName="discipline"
			@select="handleSelect"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useSearchStore } from '@stores/useSearchStore';
import SearchableRadioFilter from '@components/Filters/SearchableRadioFilter/SearchableRadioFilter.vue';
import { DisciplineFilterMapper } from '@app/routing/mappers/DisciplineFilterMapper';
import { FilterType } from '@domain/enums/FilterType';
import { FILTER_TO_FACETS_MAPPER } from '@domain/mappers/FilterToFacetsMapper';
import { SelectListItemStateType } from '@domain/enums/SelectListItemStateType';
import type ISelectItem from '@domain/interfaces/dtos/ISelectItem';
import type { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';
import { subdisciplinesAll } from '@studyportals/static-domain-data/disciplines/subdisciplines';

const searchStore = useSearchStore();
const router = useRouter();
const { filtersSelection, pageData } = storeToRefs(searchStore);

const mapper = DisciplineFilterMapper.getInstance();

const subdisciplineParentMap = new Map<string, string>();
subdisciplinesAll.forEach((sub: { id: string; disciplineId: string }) => {
	subdisciplineParentMap.set(sub.id.toString(), sub.disciplineId.toString());
});

const facetCounts = computed(() => {
	const facets = pageData.value?.filtersFacets ?? {};
	const counts = new Map<string, number>();
	const key = FILTER_TO_FACETS_MAPPER.get(FilterType.DISCIPLINES);
	if (key && facets[key]) {
		(facets as any)[key]?.forEach((facet: { value: string; count: number }) => counts.set(facet.value, facet.count));
	}
	return counts;
});

const selectedId = computed<string | number | null>(() =>
	filtersSelection.value.find((f) => f.type === FilterType.DISCIPLINES)?.values[0]?.id ?? null
);

const subdisciplinesByDiscipline = computed(() => {
	const grouped = new Map<string, ISelectItem[]>();
	mapper.getSubdisciplines().forEach((sub) => {
		const parentId = subdisciplineParentMap.get(sub.id);
		if (!parentId) return;
		const list = grouped.get(parentId) ?? [];
		list.push({
			value: sub.id,
			text: sub.name ?? sub.id,
			icon: sub.icon,
			count: facetCounts.value.get(sub.id),
			state: selectedId.value === sub.id ? SelectListItemStateType.SELECTED : SelectListItemStateType.ENABLED
		});
		grouped.set(parentId, list);
	});
	return grouped;
});

const items = computed(() => {
	return mapper.getDisciplines().map((discipline) => {
		const subItems = subdisciplinesByDiscipline.value.get(discipline.id);
		return {
			item: {
			value: discipline.id,
			text: discipline.name ?? discipline.id,
			icon: discipline.icon,
			count: facetCounts.value.get(discipline.id),
			state: selectedId.value === discipline.id ? SelectListItemStateType.SELECTED : SelectListItemStateType.ENABLED
		} as ISelectItem,
		subItems
	};
});
});

const options = computed<IMultiselectOption[]>(() => {
	const opts: IMultiselectOption[] = [];
	mapper.getDisciplines().forEach((discipline) => {
		opts.push({
			value: discipline.id,
			label: discipline.name ?? discipline.id,
			icon: discipline.icon,
			count: facetCounts.value.get(discipline.id)
		});
	});
	mapper.getSubdisciplines().forEach((sub) => {
		opts.push({
			value: sub.id,
			label: sub.name ?? sub.id,
			icon: sub.icon,
			count: facetCounts.value.get(sub.id)
		});
	});
	return opts;
});

const handleSelect = async (value: string | number, selected: boolean): Promise<void> => {
	const mappedValue = mapper.getExtendedFilterValueById(String(value));
	if (!mappedValue) return;

	let nextFilters = filtersSelection.value.filter((f) => f.type !== FilterType.DISCIPLINES);

	if (selected) {
		nextFilters = [...nextFilters, { type: FilterType.DISCIPLINES, values: [mappedValue] }];
	}

	searchStore.filtersSelection = nextFilters;
	try {
		await searchStore.setRouteFromFilters(router);
		await searchStore.loadPageData(router);
	} catch (error) {
		// TODO : Handle error properly
		console.error('Failed to update discipline filter', error);
	}
};
</script>

<style scoped lang="scss">
.DisciplineFilter {
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 1.5rem;
	padding: 0 2px;
}
</style>